// The workspace's security posture (#639, D14): the score and its breakdown,
// what changed since the last scan, the compliance frameworks, the Risks and
// their mutes, and Cyber Essentials.
//
// Two readings hold everywhere on this screen, because each was a defect in the
// old product before it was a rule:
//
// - **A score is out of what could be measured.** Detection coverage is not
//   measured in this phase, so the score is "73 of a possible 90", never "73"
//   (#1410 in the old repository: an unread component lowers the ceiling; it is
//   never scored as clean). Attack paths are not part of the score (#1283).
// - **A framework's rate is never shown alone.** It is over the requirements a
//   machine decided (passed + failed), beside how many the scan assessed, how
//   many the standard defines, how many no check reaches, and how many need a
//   person (#1395, #1451).

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { ApiError, browserApi, messageOf, type Api } from "../api/http";
import {
  DEFAULT_FRAMEWORK,
  frameworkLabel,
  muteRisk,
  readFramework,
  readPosture,
  readScans,
  startScan,
  type CyberEssentialsView,
  type FrameworkDetail,
  type FrameworkResult,
  type PostureScore,
  type PostureView,
  type Risk,
  type RisksPage,
  type AvailabilityView,
  type ScanAvailability,
  type ScanFailureReason,
  type ScanRecord,
  type ScansView,
  type ScanStatus,
  type TrendPoint,
} from "../api/posture";
import { AccountName } from "./AccountName";
import { FindingsScreen } from "./FindingsPage";
import { POSTURE_TABS, type PostureTab } from "./postureTabs";
import { Severity } from "./Severity";
import { Chip, Empty, Notice, PageIntro } from "./chrome";
import { trendSegments } from "./postureTrend";
import { readableTime } from "./runPresentation";
import { ScanControl } from "./ScanControl";
import {
  CE_COVERAGE_LABEL,
  CE_DISCLAIMER,
  CE_EVIDENCE_HEADING,
  CE_INTRO,
  CE_NO_SCAN_NOTE,
  CE_NOT_OBSERVABLE_NOTE,
  CE_OUT_OF_SCOPE_HEADING,
  CE_STATUS_LABEL,
} from "./cyberEssentialsCopy";

export interface PostureActions {
  /** One framework's requirements for one scan; rejects with the route's refusal. */
  openFramework(scanId: string, frameworkId: string): Promise<FrameworkDetail>;
  /** Accept a Risk, or re-open one; the page is read again after. */
  muteRisk(riskId: string, muted: boolean, reason: string): Promise<void>;
  /** Start a scan of one account (#821); the page is read again after. */
  runScan(cloudAccountId: string): Promise<void>;
}

/** A refused read says what the person cannot do, not which permission string it lacked. */
export function postureMessage(error: unknown, act = "view posture"): string {
  if (error instanceof ApiError && error.status === 403) {
    return `You don't have permission to ${act}.`;
  }
  return messageOf(error);
}

const round1 = (value: number): string => value.toFixed(1).replace(/\.0$/, "");

/** "38 of a possible 50", or "38 / 100" when every component was read. */
export function scoreText(score: number, max: number): string {
  return max < 100 ? `${score} of a possible ${round1(max)}` : `${score} / 100`;
}

const UNSCORED_WHY: Record<string, string> = {
  coverage_unread: "detection coverage is not measured yet",
  risks_unread: "the Risks could not be read",
  findings_unread: "the findings could not be read",
  no_account_scored_this_component: "no account could be measured for this",
};

const ACTIVE: ReadonlySet<ScanStatus> = new Set(["running", "ingesting"]);

/** Nothing to offer and nothing under way: what the page reads when the scans could not be. */
const NO_SCANS: ScansView = { accounts: [], items: [], may_scan: false };

/** The scan of this account still under way, if there is one: the list is newest first. */
const activeScan = (scans: ScansView, cloudAccountId: string): ScanRecord | undefined =>
  scans.items.find((s) => s.cloud_account_id === cloudAccountId && ACTIVE.has(s.status));

/**
 * A scan of this account under way, or Run scan as the server answers it for the account
 * (#1274): pressed when it may start, and otherwise why not.
 */
function AccountScan({
  account,
  scans,
  scanRoles,
  onRun,
}: {
  account: ScanAvailability;
  scans: ScansView;
  scanRoles: string[];
  onRun: PostureActions["runScan"];
}) {
  const active = activeScan(scans, account.cloud_account_id);
  if (active !== undefined) {
    return (
      <span role="status">
        {active.status === "ingesting"
          ? "Reading the scan's results"
          : `Scanning since ${readableTime(active.started_at)}`}
      </span>
    );
  }
  return <ScanControl account={account} scanRoles={scanRoles} onRun={onRun} />;
}

/** A date for a chart's label: the day is what a trend of weekly scans turns on. */
const readableDate = (value: string): string =>
  new Date(value).toLocaleDateString("en-GB", { dateStyle: "medium", timeZone: "UTC" });

const TREND_BOX = { width: 320, height: 64 };
/** Room around the plot so a marker at an edge is not clipped. */
const TREND_PAD = 6;

function TrendEnd({ point }: { point: TrendPoint }) {
  return (
    <span className="posture-trend__end">
      <b className="tab">{scoreText(point.score, point.score_max)}</b>{" "}
      <time className="muted" dateTime={point.computed_at}>
        {readableDate(point.computed_at)}
      </time>
    </span>
  );
}

/**
 * The score over its scans, drawn to scale (#814): a fixed axis from nothing to all
 * that could be earned, time along the other. The first and last scans are named
 * under the chart, and every scan's score is a list a screen reader, or anyone, can
 * open. A line joins only scans scored by one formula over one set of components
 * (#1283); where that changes, the line breaks and the chart says so.
 */
function ScoreTrend({ trend }: { trend: TrendPoint[] }) {
  const segments = trendSegments(trend, TREND_BOX);
  const coords = segments.flat();
  if (coords.length === 0) return null;
  const first = coords[0]!.point;
  const last = coords[coords.length - 1]!.point;
  const ends = segments.flatMap((segment) =>
    segment.length === 1 ? [segment[0]!] : [segment[0]!, segment[segment.length - 1]!],
  );
  const width = TREND_BOX.width + 2 * TREND_PAD;
  const height = TREND_BOX.height + 2 * TREND_PAD;
  return (
    <figure className="posture-trend panel__body" data-testid="posture-trend">
      <svg
        className="posture-trend__chart"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-labelledby="posture-trend-ends"
      >
        <line
          className="posture-trend__axis"
          x1={0}
          x2={width}
          y1={TREND_PAD}
          y2={TREND_PAD}
          vectorEffect="non-scaling-stroke"
        />
        <line
          className="posture-trend__axis"
          x1={0}
          x2={width}
          y1={TREND_BOX.height + TREND_PAD}
          y2={TREND_BOX.height + TREND_PAD}
          vectorEffect="non-scaling-stroke"
        />
        {segments
          .filter((segment) => segment.length > 1)
          .map((segment) => (
            <polyline
              key={segment[0]!.point.scan_id}
              className="posture-trend__line"
              points={segment.map((c) => `${c.x + TREND_PAD},${c.y + TREND_PAD}`).join(" ")}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        {ends.map((c, i) => (
          <line
            key={i}
            className="posture-trend__mark"
            x1={c.x + TREND_PAD}
            x2={c.x + TREND_PAD}
            y1={c.y + TREND_PAD}
            y2={c.y + TREND_PAD}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      <figcaption id="posture-trend-ends" className="posture-trend__ends">
        <span className="sr-only">
          {coords.length === 1
            ? "Posture score from one scan: "
            : `Posture score over ${coords.length} scans, from `}
        </span>
        <TrendEnd point={first} />
        {coords.length === 1 ? null : (
          <>
            <span className="sr-only"> to </span>
            <TrendEnd point={last} />
          </>
        )}
      </figcaption>
      {segments.length > 1 ? (
        <p className="muted" data-testid="posture-trend-break">
          The score was not computed the same way across these scans, so the line is broken
          where that changed.
        </p>
      ) : null}
      <details>
        <summary>Every scan&apos;s score</summary>
        <ol className="stack stack--tight">
          {coords.map(({ point }) => (
            <li key={point.scan_id}>
              {readableTime(point.computed_at)}: {scoreText(point.score, point.score_max)}
            </li>
          ))}
        </ol>
      </details>
    </figure>
  );
}

function ScorePanel({
  score,
  scans,
  availability,
  onRun,
}: {
  score: PostureScore;
  scans: ScansView;
  /** Whether each account can be scanned; null when it could not be read. */
  availability: AvailabilityView | null;
  onRun: PostureActions["runScan"];
}) {
  const scanRoles = availability?.scan_roles ?? [];
  if (!score.has_data || score.score === null) {
    return (
      <section className="panel">
        <div className="panel__head">
          <h2>Posture score</h2>
        </div>
        <Empty title="No account has been scanned yet.">
          <p className="muted">A score appears after the first completed scan of an account.</p>
          {(availability?.accounts ?? []).map((account, _at, all) => (
            <div key={account.cloud_account_id}>
              {all.length === 1 ? null : <AccountName id={account.account_id} name={account.name} />}{" "}
              <AccountScan account={account} scans={scans} scanRoles={scanRoles} onRun={onRun} />
            </div>
          ))}
        </Empty>
      </section>
    );
  }
  const scannable = new Map((availability?.accounts ?? []).map((a) => [a.account_id, a]));
  const unscored = score.components.filter((c) => !c.scored);
  return (
    <section className="panel" data-testid="posture-score">
      <div className="panel__head">
        <h2>Posture score</h2>
        <Chip>{score.account_count === 1 ? "1 account" : `${score.account_count} accounts`}</Chip>
      </div>
      <div className="panel__body stack stack--tight">
        {unscored.length === 0 ? (
          <p>
            <b className="tab posture-figure">{scoreText(score.score, score.score_max)}</b>
          </p>
        ) : (
          <Term term={<b className="tab posture-figure">{scoreText(score.score, score.score_max)}</b>} testId="posture-ceiling">
            {unscored.map((c) => c.label).join(" and ")} {unscored.length === 1 ? "is" : "are"} not
            measured, so {round1(100 - score.score_max)} points cannot be earned and none are
            deducted for {unscored.length === 1 ? "it" : "them"}.
          </Term>
        )}
      </div>
      <ScoreTrend trend={score.trend} />
      <div className="table-wrap" tabIndex={0}>
        <table className="table">
          <thead>
            <tr>
              <th>Component</th>
              <th className="num">Weight</th>
              <th className="num">Points lost</th>
              <th>Basis</th>
            </tr>
          </thead>
          <tbody>
            {score.components.map((c) => (
              <tr key={c.key}>
                <td>{c.label}</td>
                <td className="num">{c.weight_pct}</td>
                <td className="num">{c.scored ? round1(c.points_lost ?? 0) : "not measured"}</td>
                <td className="muted">
                  {c.scored
                    ? String(c.detail.basis ?? "")
                    : (UNSCORED_WHY[String(c.detail.unscored_reason)] ?? "not measured")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="table-wrap" tabIndex={0}>
        <table className="table">
          <thead>
            <tr>
              <th>AWS account</th>
              <th>Score</th>
              <th>Scanned</th>
              <th>Scan</th>
            </tr>
          </thead>
          <tbody>
            {score.per_account.map((a) => (
              <tr key={a.account_id}>
                <td>
                  <AccountName id={a.account_id} name={a.name} />
                </td>
                <td>
                  {a.score === null || a.score_max === undefined
                    ? "not scanned"
                    : scoreText(a.score, a.score_max)}
                </td>
                <td>{a.completed_at ? readableTime(a.completed_at) : "never"}</td>
                <td>
                  {scannable.has(a.account_id) ? (
                    <AccountScan
                      account={scannable.get(a.account_id)!}
                      scans={scans}
                      scanRoles={scanRoles}
                      onRun={onRun}
                    />
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const SCAN_STATUS: Record<ScanStatus, { words: string; tone?: "good" | "bad" | "warn" }> = {
  running: { words: "running", tone: "warn" },
  ingesting: { words: "reading results", tone: "warn" },
  done: { words: "done", tone: "good" },
  failed: { words: "failed", tone: "bad" },
};

/**
 * Why a failed scan failed, in words, for each code of the closed set (#1276). Typed over
 * `ScanFailureReason`, so a code added there without words here does not compile.
 */
const FAILURE_WORDS: Record<ScanFailureReason, string> = {
  launch_failed: "The scanner could not be started. Try again later.",
  no_output: "The scanner stopped without writing any results. Try the scan again.",
  scanner_error: "The scanner reported an error and could not scan this account.",
  malformed_output: "The scanner's results could not be read, so none were saved.",
  ingest_error: "The scanner's results could not be saved. Try the scan again.",
  account_disconnected: "The account was disconnected during the scan.",
  timed_out: "The scan did not finish in time and was ended. Try the scan again.",
};

/** A failed scan's reason in words; null for any other scan. */
const failureWords = (scan: ScanRecord): string | null =>
  scan.status !== "failed"
    ? null
    : ((scan.failure_reason === null ? undefined : FAILURE_WORDS[scan.failure_reason]) ??
      "The scan did not complete.");

/** Under a failed scan's state, why it failed, as text on the page. */
function FailureReason({ scan }: { scan: ScanRecord }) {
  const words = failureWords(scan);
  return words === null ? null : <div className="muted">{words}</div>;
}

/** The workspace's scans, newest first, each with its state (#821). */
function ScansPanel({ scans }: { scans: ScansView | null }) {
  if (scans === null) {
    return (
      <section className="panel" data-testid="posture-scans">
        <div className="panel__head">
          <h2>Scans</h2>
        </div>
        <p className="muted panel__body">The scans could not be read.</p>
      </section>
    );
  }
  if (scans.items.length === 0) return null;
  return (
    <section className="panel" data-testid="posture-scans">
      <div className="panel__head">
        <h2>Scans</h2>
      </div>
      <div className="table-wrap" tabIndex={0}>
        <table className="table">
          <thead>
            <tr>
              <th>AWS account</th>
              <th>Started</th>
              <th>State</th>
              <th className="num">Findings</th>
            </tr>
          </thead>
          <tbody>
            {scans.items.map((s) => (
              <tr key={s.scan_id} data-scan-status={s.status}>
                <td>
                  <AccountName id={s.account_id} name={s.account_name} />
                </td>
                <td>{readableTime(s.started_at)}</td>
                <td>
                  <Chip tone={SCAN_STATUS[s.status]?.tone}>
                    {SCAN_STATUS[s.status]?.words ?? s.status}
                  </Chip>
                  <FailureReason scan={s} />
                </td>
                <td className="num">{s.finding_count ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** The drift banner. It never characterises attack paths: none are measured. */
export function DriftBanner({ drift }: { drift: PostureScore["drift"] }) {
  if (drift === null) return null;
  if (!drift.has_baseline) {
    return (
      <Notice>
        First scan of account {drift.account_id}: its {drift.new_findings_count} failing findings
        are the baseline the next scan is compared with.
      </Notice>
    );
  }
  return (
    <Notice tone={drift.new_findings_count > 0 || drift.newly_exposed_count > 0 ? "bad" : "good"}>
      Since the previous scan of account {drift.account_id}: {drift.new_findings_count} new
      failing, {drift.resolved_findings_count} resolved, {drift.newly_exposed_count} newly
      reachable from the internet.
    </Notice>
  );
}

function RateCells({ result }: { result: Omit<FrameworkResult, "framework_id"> }) {
  return (
    <>
      <td className="num">
        <b>{round1(result.pass_rate)}%</b>
        <div className="muted">
          {result.passed} of {result.passed + result.failed} decided
        </div>
      </td>
      <td className="num">{result.manual}</td>
      <td className="num">
        {result.total} of {result.framework_total}
      </td>
      <td className="num">{result.not_assessed}</td>
    </>
  );
}

/**
 * A term and the method behind it, closed until asked for. A native disclosure, so
 * Tab reaches it and Enter or a tap opens it; a `title` reaches neither (#799).
 */
function Term({ term, testId, children }: { term: ReactNode; testId?: string; children: ReactNode }) {
  return (
    <details className="term" data-testid={testId}>
      <summary>{term}</summary>
      <p className="term__method">{children}</p>
    </details>
  );
}

/** How a pass rate is computed, on the term rather than above the table (#799). */
const PASS_RATE_METHOD =
  "A pass rate is over the requirements a scan could decide. Requirements that need a person are counted and left out of it, and a requirement no check reaches is not assessed at all.";

/** What a Risk is, in a disclosure under the panel's heading (#799). */
const RISK_MEANING =
  "A Risk is two or more signals on one resource. Accepting one holds only while its signals stay the ones you accepted.";

const RATE_HEAD = (
  <>
    <th className="num">
      <Term term="Pass rate">{PASS_RATE_METHOD}</Term>
    </th>
    <th className="num">Need a person</th>
    <th className="num">Assessed</th>
    <th className="num">Not assessed</th>
  </>
);

/**
 * That the scan is read against another Prowler's framework definitions than the one that
 * ran it (#1279; the owner's decision 2 on #1273), in words, or null when it is not.
 */
function Reassessed({ id, result }: { id: string; result: FrameworkResult | undefined }) {
  if (!result?.reassessed_under) return null;
  const ran =
    result.scanned_with == null
      ? "This scan did not record which Prowler ran it."
      : `This scan ran Prowler ${result.scanned_with}.`;
  // One run of text, so a screen reader announces it as written: an element inside it
  // would be read with a pause before the full stop (#1279's first `web` run).
  return (
    <Notice id={id}>
      {`Reassessed under Prowler ${result.reassessed_under} definitions. ${ran} Its frameworks ` +
        `are read as Prowler ${result.reassessed_under} defines them.`}
    </Notice>
  );
}

function CompliancePanel({
  compliance,
  scanId,
  onOpen,
}: {
  compliance: FrameworkResult[];
  scanId: string | null;
  onOpen: PostureActions["openFramework"];
}) {
  // The framework whose requirements are shown. The tab arrives on the default (#1279) when
  // this scan assessed it; a press moves it.
  const arrival = compliance.some((f) => f.framework_id === DEFAULT_FRAMEWORK)
    ? DEFAULT_FRAMEWORK
    : null;
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<FrameworkDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const noteId = useId();
  // Only the latest press's answer is shown, whichever answer arrives last.
  const asked = useRef<string | null>(null);
  const reader = useRef(onOpen);
  reader.current = onOpen;
  const open = useCallback(
    (frameworkId: string) => {
      if (scanId === null) return;
      asked.current = frameworkId;
      setSelected(frameworkId);
      setError(null);
      reader.current(scanId, frameworkId).then(
        (read) => {
          if (asked.current === frameworkId) setDetail(read);
        },
        (e: unknown) => {
          if (asked.current === frameworkId) setError(postureMessage(e, "view this framework"));
        },
      );
    },
    [scanId],
  );
  useEffect(() => {
    if (arrival !== null) open(arrival);
  }, [arrival, open]);
  const reassessed = compliance[0]?.reassessed_under ? noteId : undefined;
  return (
    <section className="panel" data-testid="posture-compliance">
      <div className="panel__head">
        <h2>Compliance frameworks</h2>
        {scanId === null ? null : <Chip>latest scan</Chip>}
      </div>
      <Reassessed id={noteId} result={compliance[0]} />
      {compliance.length === 0 ? (
        <Empty title="No framework has been assessed yet." />
      ) : (
        <div className="table-wrap" tabIndex={0}>
          <table className="table">
            <thead>
              <tr>
                <th>Framework</th>
                {RATE_HEAD}
                <th />
              </tr>
            </thead>
            <tbody>
              {compliance.map((f) => (
                <tr key={f.framework_id} data-framework={f.framework_id}>
                  <td>{frameworkLabel(f.framework_id)}</td>
                  <RateCells result={f} />
                  <td>
                    <button
                      type="button"
                      className="btn btn--small"
                      aria-label={`${frameworkLabel(f.framework_id)} requirements`}
                      aria-pressed={selected === f.framework_id}
                      aria-describedby={reassessed}
                      onClick={() => open(f.framework_id)}
                    >
                      Requirements
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {detail === null ? null : <FrameworkDetailView detail={detail} />}
    </section>
  );
}

export function FrameworkDetailView({ detail }: { detail: FrameworkDetail }) {
  return (
    <div className="panel__body stack" data-testid="framework-detail">
      <h3>{frameworkLabel(detail.framework_id)}</h3>
      <p>
        <b>{round1(detail.pass_rate)}%</b> of the {detail.passed + detail.failed} requirements
        this scan decided · {detail.manual} need a person · {detail.total} of{" "}
        {detail.framework_total} assessed · {detail.not_assessed} not assessed
      </p>
      <div className="table-wrap" tabIndex={0}>
        <table className="table">
          <thead>
            <tr>
              <th>Requirement</th>
              <th>Title</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {detail.requirements.map((r) => (
              <tr key={r.requirement_id}>
                <td>{r.requirement_id}</td>
                <td>{r.title}</td>
                <td>
                  <Chip tone={r.status === "FAIL" ? "bad" : r.status === "PASS" ? "good" : "warn"}>
                    {r.status === "MANUAL" ? "needs a person" : r.status}
                  </Chip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RiskRow({
  risk,
  mayMute,
  onMute,
}: {
  risk: Risk;
  mayMute: boolean;
  onMute: PostureActions["muteRisk"];
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const act = (muted: boolean) => {
    setBusy(true);
    setError(null);
    onMute(risk.risk_id, muted, "").then(
      () => setBusy(false),
      (e: unknown) => {
        setBusy(false);
        setError(postureMessage(e, "accept or re-open this Risk"));
      },
    );
  };
  return (
    <tr>
      <td>
        {risk.resource_name || risk.resource_arn}
        <div className="muted">{risk.resource_arn}</div>
      </td>
      <td>{risk.signals.join(" + ")}</td>
      <td>
        <Severity level={risk.severity} />
      </td>
      <td>
        {risk.muted ? <Chip>accepted</Chip> : <Chip tone="bad">active</Chip>}
        {risk.mute_lapsed ? (
          <div className="muted">
            Was accepted; it has since gained {risk.unaccepted_signals.join(", ")}.
          </div>
        ) : null}
        {error === null ? null : <div className="muted">{error}</div>}
      </td>
      <td>
        {mayMute ? (
          <button
            type="button"
            className="btn btn--small"
            disabled={busy}
            onClick={() => act(!risk.muted)}
          >
            {risk.muted ? "Re-open" : "Accept"}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function RisksPanel({ risks, onMute }: { risks: RisksPage; onMute: PostureActions["muteRisk"] }) {
  return (
    <section className="panel" data-testid="posture-risks">
      <div className="panel__head">
        <h2>Risks</h2>
        <Chip>
          {risks.active} active · {risks.muted} accepted
        </Chip>
      </div>
      <div className="panel__body stack stack--tight">
        <Term term="What counts as a Risk">{RISK_MEANING}</Term>
        {risks.may_mute ? null : (
          <p className="muted">Accepting a Risk is this workspace&apos;s own decision.</p>
        )}
      </div>
      {risks.items.length === 0 ? (
        <Empty title="No resource carries two signals." />
      ) : (
        <div className="table-wrap" tabIndex={0}>
          <table className="table">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Signals</th>
                <th>Severity</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {risks.items.map((r) => (
                <RiskRow key={r.risk_id} risk={r} mayMute={risks.may_mute} onMute={onMute} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function CyberEssentialsPanel({ view }: { view: CyberEssentialsView }) {
  return (
    <section className="panel" data-testid="cyber-essentials">
      <div className="panel__head">
        <h2>{view.scheme}</h2>
      </div>
      <div className="panel__body stack stack--tight">
        <p data-testid="ce-disclaimer">
          <b>{CE_DISCLAIMER}</b>
        </p>
        {view.scan_ran ? null : <p className="muted">{CE_NO_SCAN_NOTE}</p>}
        <p>{CE_INTRO}</p>
      </div>
      {/* Collapsed until asked for (#814): the disclaimer and the scope stay in view. */}
      <details className="posture-controls">
        <summary className="panel__body">What each control covers</summary>
        {view.controls.map((c) => (
          <div key={c.id} className="panel__body stack stack--tight" data-control={c.id}>
            <h3>
              {c.name} <Chip>{CE_STATUS_LABEL[c.status] ?? c.status}</Chip>{" "}
              <Chip>{CE_COVERAGE_LABEL[c.coverage] ?? c.coverage}</Chip>
            </h3>
            <div>
              <h4>{CE_EVIDENCE_HEADING}</h4>
              <p>{c.what_we_evidence}</p>
              {c.status === "NOT_OBSERVABLE" ? <p className="muted">{CE_NOT_OBSERVABLE_NOTE}</p> : null}
            </div>
            <div>
              <h4>{CE_OUT_OF_SCOPE_HEADING}</h4>
              <p>{c.out_of_scope}</p>
            </div>
          </div>
        ))}
      </details>
    </section>
  );
}

/** Posture's tabs (#814): real tabs, so arrows, Home and End move between them. */
function PostureTabs({ tab, onTab }: { tab: PostureTab; onTab: (tab: PostureTab) => void }) {
  const move = (event: KeyboardEvent<HTMLButtonElement>, current: PostureTab) => {
    const index = POSTURE_TABS.findIndex((t) => t.id === current);
    const count = POSTURE_TABS.length;
    const next =
      event.key === "ArrowRight"
        ? (index + 1) % count
        : event.key === "ArrowLeft"
          ? (index + count - 1) % count
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? count - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    const chosen = POSTURE_TABS[next]!.id;
    onTab(chosen);
    document.getElementById(`posture-tab-${chosen}`)?.focus();
  };
  return (
    <div className="posture-tabs" role="tablist" aria-label="Posture sections">
      {POSTURE_TABS.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          id={`posture-tab-${id}`}
          role="tab"
          aria-selected={tab === id}
          aria-controls={`posture-panel-${id}`}
          tabIndex={tab === id ? 0 : -1}
          onKeyDown={(event) => move(event, id)}
          onClick={() => onTab(id)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function TabPanel({ id, tab, children }: { id: PostureTab; tab: PostureTab; children: ReactNode }) {
  return (
    <div
      id={`posture-panel-${id}`}
      className="posture-panel"
      role="tabpanel"
      aria-labelledby={`posture-tab-${id}`}
      hidden={tab !== id}
    >
      {children}
    </div>
  );
}

export function PosturePage({
  posture,
  error,
  actions,
  tab = "score",
  onTab = () => {},
  findings = null,
}: {
  posture: PostureView | null;
  error: string | null;
  actions: PostureActions;
  /** The tab shown; the location holds it (`postureTabs.ts`). */
  tab?: PostureTab;
  onTab?: (tab: PostureTab) => void;
  /** The Findings tab's screen, mounted only while that tab is shown: it reads its own. */
  findings?: ReactNode;
}) {
  const reading =
    posture === null && error === null ? (
      <section className="panel">
        <p className="muted panel__body" role="status">
          Reading this workspace&apos;s posture…
        </p>
      </section>
    ) : null;
  return (
    <>
      <PageIntro
        destination="posture"
        action={
          posture?.overview.last_scan_at ? (
            <Chip>last scan {readableTime(posture.overview.last_scan_at)}</Chip>
          ) : null
        }
      />
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <PostureTabs tab={tab} onTab={onTab} />
      <TabPanel id="score" tab={tab}>
        {reading}
        {posture === null ? null : (
          <>
            <DriftBanner drift={posture.score.drift} />
            <ScorePanel
              score={posture.score}
              scans={posture.scans ?? NO_SCANS}
              availability={posture.availability}
              onRun={actions.runScan}
            />
            <ScansPanel scans={posture.scans} />
          </>
        )}
      </TabPanel>
      <TabPanel id="findings" tab={tab}>
        {tab === "findings" ? findings : null}
      </TabPanel>
      <TabPanel id="risks" tab={tab}>
        {reading}
        {posture === null ? null : <RisksPanel risks={posture.risks} onMute={actions.muteRisk} />}
      </TabPanel>
      <TabPanel id="frameworks" tab={tab}>
        {reading}
        {posture === null ? null : (
          <>
            <CompliancePanel
              compliance={posture.compliance}
              scanId={posture.overview.last_scan_id}
              onOpen={actions.openFramework}
            />
            <CyberEssentialsPanel view={posture.cyberEssentials} />
          </>
        )}
      </TabPanel>
    </>
  );
}

/** How often a scan under way is read again. */
const SCAN_POLL_MS = 15_000;

/**
 * The screen as the console mounts it: reads the posture on arrival, and again
 * after a mute, so the page shows what the workspace now holds and never what was
 * sent. The console keys it on the workspace, so a context change reads afresh.
 */
export function PostureScreen({
  api = browserApi,
  tab = "score",
  onTab,
}: {
  api?: Api;
  tab?: PostureTab;
  onTab?: (tab: PostureTab) => void;
}) {
  const [posture, setPosture] = useState<PostureView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const read = useCallback(
    () =>
      readPosture(api).then(
        (value) => {
          setPosture(value);
          setError(null);
        },
        (reason: unknown) => setError(postureMessage(reason)),
      ),
    [api],
  );
  useEffect(() => {
    void read();
  }, [read]);
  // How many times a scan has finished under this screen. The Findings tab reads its own
  // findings when it mounts, so it is mounted afresh on each (#866): a person who stays on
  // it sees the finished scan's findings, not the previous scan's.
  const [finished, setFinished] = useState(0);
  // While a scan is under way, its state is read again every so often, and the whole
  // page once none is: a finished scan brings a new score with it.
  const scanning = posture?.scans?.items.some((s) => ACTIVE.has(s.status)) ?? false;
  useEffect(() => {
    if (!scanning) return;
    const timer = setInterval(() => {
      readScans(api).then(
        (scans) => {
          if (scans.items.some((s) => ACTIVE.has(s.status))) {
            setPosture((current) => (current === null ? current : { ...current, scans }));
          } else {
            void read().then(() => setFinished((count) => count + 1));
          }
        },
        () => {},
      );
    }, SCAN_POLL_MS);
    return () => clearInterval(timer);
  }, [api, read, scanning]);
  const actions: PostureActions = {
    openFramework: (scanId, frameworkId) => readFramework(api, scanId, frameworkId),
    muteRisk: async (riskId, muted, reason) => {
      await muteRisk(api, riskId, muted, reason);
      await read();
    },
    runScan: async (cloudAccountId) => {
      await startScan(api, cloudAccountId);
      await read();
    },
  };
  return (
    <PosturePage
      posture={posture}
      error={error}
      actions={actions}
      tab={tab}
      onTab={onTab}
      findings={<FindingsScreen key={finished} api={api} scans={posture?.scans?.items} />}
    />
  );
}
